import React, { useState, useEffect } from "react";
import axios from "axios";
import { BookOpen, Users, BarChart3, Building2, Clock, Search, GraduationCap, ChevronRight } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const DEPT_COLORS = {
  "Computer Science & Engineering": "from-blue-600 to-blue-800",
  "Electronics & Communication Engineering": "from-purple-600 to-purple-800",
  "Mechanical Engineering": "from-orange-600 to-orange-800",
  "Administration & Finance": "from-slate-600 to-slate-800",
};

const SECTION_STATUS_CFG = {
  OPEN:      { label: "Open",      bg: "bg-emerald-100 text-emerald-700" },
  PLANNED:   { label: "Planned",   bg: "bg-blue-100 text-blue-700" },
  CLOSED:    { label: "Closed",    bg: "bg-slate-100 text-slate-500" },
  CANCELLED: { label: "Cancelled", bg: "bg-red-100 text-red-700" },
};

export default function WorkloadAssignments() {
  const token = localStorage.getItem("academiaos_token");
  const headers = { Authorization: `Bearer ${token}` };

  const [workers, setWorkers] = useState([]);
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [workload, setWorkload] = useState(null);
  const [stats, setStats] = useState(null);
  const [institutionSummary, setInstitutionSummary] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [activeView, setActiveView] = useState("detail"); // "detail" | "stats"

  useEffect(() => {
    fetchWorkers();
    fetchStats();
    fetchInstitutionSummary();
  }, []);

  useEffect(() => {
    if (selectedWorker) fetchWorkload(selectedWorker.worker_id);
  }, [selectedWorker]);

  const fetchWorkers = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/v1/peo01/workers`, { headers, params: { limit: 50 } });
      const list = res.data.workers || [];
      setWorkers(list);
      if (list.length > 0) setSelectedWorker(list[0]);
    } catch (e) {
      console.error("Workers fetch failed", e);
    } finally {
      setLoading(false);
    }
  };

  const fetchWorkload = async (workerId) => {
    setDetailLoading(true);
    try {
      const res = await axios.get(`${API}/api/v1/peo02/workload/faculty/${workerId}`, { headers });
      setWorkload(res.data);
    } catch (e) {
      console.error("Workload fetch failed", e);
      setWorkload(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/api/v1/peo02/workload/stats`, { headers });
      setStats(res.data);
    } catch (e) { console.error(e); }
  };

  const fetchInstitutionSummary = async () => {
    try {
      const res = await axios.get(`${API}/api/v1/peo02/workload/institution-summary`, { headers });
      setInstitutionSummary(res.data || []);
    } catch (e) { console.error(e); }
  };

  const filteredWorkers = workers.filter(w =>
    !search || w.display_name?.toLowerCase().includes(search.toLowerCase()) ||
    w.worker_number?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="flex h-full bg-slate-950 text-slate-100">
      {/* ── Worker List Panel ─────────────────────────────────────────── */}
      <div className="w-72 border-r border-slate-800 flex flex-col">
        <div className="p-4 border-b border-slate-800">
          <h2 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
            <GraduationCap size={14} className="text-violet-400" />
            Faculty & Staff
          </h2>
          <div className="relative">
            <Search size={13} className="absolute left-2.5 top-2.5 text-slate-500" />
            <input
              data-testid="workload-worker-search"
              className="w-full bg-slate-900 border border-slate-700 rounded-lg pl-8 pr-3 py-2 text-xs text-slate-300 placeholder-slate-500 focus:outline-none focus:border-violet-500"
              placeholder="Search faculty…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
        </div>

        {/* Stats strip */}
        {stats && (
          <div className="grid grid-cols-2 gap-0 border-b border-slate-800 text-center">
            {[
              { label: "Sections", val: stats.total_sections, color: "text-violet-400" },
              { label: "Instructors", val: stats.instructors_assigned, color: "text-teal-400" },
            ].map(s => (
              <div key={s.label} className="py-3 border-r border-slate-800 last:border-r-0">
                <div className={`text-xl font-bold ${s.color}`}>{s.val}</div>
                <div className="text-[10px] text-slate-500 mt-0.5">{s.label}</div>
              </div>
            ))}
          </div>
        )}

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-4 text-center text-xs text-slate-500">Loading…</div>
          ) : filteredWorkers.map(w => {
            const instRow = institutionSummary.find(i => i.worker_id === w.worker_id);
            return (
              <button
                key={w.worker_id}
                data-testid={`workload-worker-${w.worker_id}`}
                onClick={() => setSelectedWorker(w)}
                className={`w-full text-left px-4 py-3 border-b border-slate-800/60 hover:bg-slate-800/40 transition-colors ${selectedWorker?.worker_id === w.worker_id ? "bg-slate-800 border-l-2 border-l-violet-500" : ""}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-violet-700 to-violet-900 flex items-center justify-center text-xs font-bold text-violet-200 flex-shrink-0">
                      {(w.display_name || "?")[0]}
                    </div>
                    <div className="min-w-0">
                      <div className="text-xs font-medium text-slate-200 truncate">{w.display_name}</div>
                      <div className="text-[10px] text-slate-500 truncate">{w.department_name || "—"}</div>
                    </div>
                  </div>
                  {instRow && instRow.total_sections > 0 && (
                    <div className="flex-shrink-0 bg-violet-900/50 text-violet-300 text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                      {instRow.total_sections}
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Main Content ──────────────────────────────────────────────── */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {selectedWorker ? (
          <>
            {/* Header */}
            <div className="border-b border-slate-800 px-6 py-4 flex items-center justify-between flex-shrink-0">
              <div>
                <h1 className="text-base font-semibold text-slate-100" data-testid="workload-employee-name">
                  {selectedWorker.display_name}
                </h1>
                <p className="text-xs text-slate-500">
                  {selectedWorker.worker_number} · {selectedWorker.department_name} · {selectedWorker.job_title || "Faculty"}
                </p>
              </div>

              {/* View toggle */}
              <div className="flex bg-slate-900 rounded-lg p-1 gap-1">
                {[
                  { key: "detail", label: "Assignments", icon: BookOpen },
                  { key: "stats", label: "Institution Stats", icon: BarChart3 },
                ].map(v => (
                  <button
                    key={v.key}
                    data-testid={`workload-view-${v.key}`}
                    onClick={() => setActiveView(v.key)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${activeView === v.key ? "bg-violet-600 text-white" : "text-slate-400 hover:text-slate-200"}`}
                  >
                    <v.icon size={12} />
                    {v.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto p-6">
              {activeView === "detail" ? (
                <FacultyWorkloadDetail workload={workload} loading={detailLoading} />
              ) : (
                <InstitutionStatsView stats={stats} institutionSummary={institutionSummary} />
              )}
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center text-slate-500">
              <BookOpen size={32} className="mx-auto mb-3 opacity-30" />
              <p className="text-sm">Select a faculty member to view workload</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Faculty Workload Detail ───────────────────────────────────────────────────
function FacultyWorkloadDetail({ workload, loading }) {
  if (loading) return <div className="text-center py-10 text-xs text-slate-500">Loading workload…</div>;
  if (!workload) return (
    <div className="text-center py-10 text-slate-600">
      <BookOpen size={28} className="mx-auto mb-2 opacity-30" />
      <p className="text-xs">No workload data found for this employee</p>
    </div>
  );

  return (
    <div className="space-y-5">
      {/* Summary stats */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { label: "Total Sections", val: workload.total_sections, color: "text-violet-400" },
          { label: "Contact Hours/wk", val: `${workload.total_contact_hours}h`, color: "text-teal-400" },
          { label: "Department", val: workload.department_name || "—", color: "text-slate-300", small: true },
          { label: "Job Title", val: workload.job_title || "Faculty", color: "text-slate-300", small: true },
        ].map(s => (
          <div key={s.label} className="bg-slate-900 border border-slate-800 rounded-xl p-4 text-center">
            {s.small ? (
              <div className={`text-xs font-medium ${s.color} break-words leading-tight`}>{s.val}</div>
            ) : (
              <div className={`text-3xl font-bold ${s.color}`}>{s.val}</div>
            )}
            <div className="text-[10px] text-slate-500 mt-1">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Section assignments */}
      {workload.assignments.length > 0 ? (
        <div>
          <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">Course Sections</h3>
          <div className="space-y-3">
            {workload.assignments.map(a => {
              const scfg = SECTION_STATUS_CFG[a.status] || { label: a.status, bg: "bg-slate-100 text-slate-500" };
              const deptGrad = DEPT_COLORS[a.department_name] || "from-slate-700 to-slate-800";
              return (
                <div key={a.section_id} data-testid={`section-${a.section_id}`}
                  className="bg-slate-900 border border-slate-800 hover:border-slate-700 rounded-2xl p-4 transition-colors"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3 min-w-0">
                      <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${deptGrad} flex items-center justify-center text-xs font-bold text-white flex-shrink-0`}>
                        {a.course_code?.split("-")?.[1]?.slice(0, 2) || "CS"}
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-slate-200">{a.course_title}</div>
                        <div className="text-xs text-slate-500 mt-0.5">{a.course_code} · {a.section_code}</div>
                        <div className="flex items-center gap-3 mt-2 text-[10px] text-slate-500">
                          {a.schedule_block && (
                            <span className="flex items-center gap-1">
                              <Clock size={10} className="text-slate-600" />
                              {a.schedule_block}
                            </span>
                          )}
                          {a.room && (
                            <span className="flex items-center gap-1">
                              <Building2 size={10} className="text-slate-600" />
                              {a.room}
                            </span>
                          )}
                          {a.capacity && (
                            <span className="flex items-center gap-1">
                              <Users size={10} className="text-slate-600" />
                              Cap: {a.capacity}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex-shrink-0 flex flex-col items-end gap-2">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${scfg.bg}`}>
                        {scfg.label}
                      </span>
                      <div className="text-[10px] text-slate-500">{a.term_code}</div>
                      <div className="text-[10px] text-violet-400 font-medium">{a.weekly_contact_hours}h/wk</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="text-center py-10 border border-dashed border-slate-800 rounded-2xl">
          <BookOpen size={24} className="mx-auto mb-2 text-slate-700" />
          <p className="text-xs text-slate-600">No course sections assigned this semester</p>
          <p className="text-[10px] text-slate-700 mt-1">(Synthetic demo — only faculty with teaching assignments are shown)</p>
        </div>
      )}
    </div>
  );
}

// ── Institution Stats View ────────────────────────────────────────────────────
function InstitutionStatsView({ stats, institutionSummary }) {
  return (
    <div className="space-y-6">
      {/* Top stats */}
      {stats && (
        <div>
          <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">Institution Workload Overview</h3>
          <div className="grid grid-cols-4 gap-3">
            {[
              { label: "Total Sections", val: stats.total_sections, color: "text-violet-400" },
              { label: "Assigned Faculty", val: stats.instructors_assigned, color: "text-teal-400" },
              { label: "Courses", val: stats.courses, color: "text-blue-400" },
              { label: "Active Terms", val: stats.terms, color: "text-amber-400" },
            ].map(s => (
              <div key={s.label} className="bg-slate-900 border border-slate-800 rounded-xl p-4 text-center">
                <div className={`text-3xl font-bold ${s.color}`}>{s.val}</div>
                <div className="text-[10px] text-slate-500 mt-1">{s.label}</div>
              </div>
            ))}
          </div>

          {/* By department */}
          {stats.by_department?.length > 0 && (
            <div className="mt-5 bg-slate-900 border border-slate-800 rounded-xl p-5">
              <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-4">Sections by Department</h4>
              <div className="space-y-3">
                {stats.by_department.map(d => {
                  const pct = stats.total_sections > 0 ? (d.sections / stats.total_sections) * 100 : 0;
                  return (
                    <div key={d.department}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-slate-300">{d.department}</span>
                        <span className="text-xs text-slate-500">{d.sections} sections</span>
                      </div>
                      <div className="bg-slate-800 rounded-full h-2">
                        <div
                          className="bg-violet-500 h-2 rounded-full transition-all"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Faculty workload table */}
      {institutionSummary.length > 0 && (
        <div>
          <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">All Faculty Workload</h3>
          <div className="overflow-hidden rounded-xl border border-slate-800">
            <table className="w-full text-xs" data-testid="faculty-workload-table">
              <thead>
                <tr className="bg-slate-900/60">
                  <th className="px-4 py-2.5 text-left text-slate-500 font-medium">Faculty</th>
                  <th className="px-4 py-2.5 text-left text-slate-500 font-medium">Department</th>
                  <th className="px-4 py-2.5 text-right text-slate-500 font-medium">Sections</th>
                  <th className="px-4 py-2.5 text-right text-slate-500 font-medium">Contact Hrs/wk</th>
                </tr>
              </thead>
              <tbody>
                {institutionSummary.map((r, i) => (
                  <tr key={r.worker_id} className={`border-t border-slate-800/60 ${i % 2 === 0 ? "bg-slate-900/20" : ""}`}>
                    <td className="px-4 py-3">
                      <div className="text-slate-200 font-medium">{r.employee_name}</div>
                      <div className="text-slate-600 text-[10px]">{r.worker_number}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-400">{r.department || "—"}</td>
                    <td className="px-4 py-3 text-right">
                      <span className={`font-bold ${r.total_sections > 0 ? "text-violet-400" : "text-slate-600"}`}>
                        {r.total_sections}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right text-teal-400 font-medium">
                      {r.total_contact_hours > 0 ? `${r.total_contact_hours}h` : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-2 text-[10px] text-slate-700 text-center">Synthetic demo data — VCE Academic Year 2025-26</p>
        </div>
      )}
    </div>
  );
}
